<template>
  <section class="cards-section">
    <div class="cards-wrapper">
      <div 
        v-for="(card, index) in cards" 
        :key="index"
        class="card-full-screen"
        :class="{ 
          'visible': card.visible,
          'image-right': index % 2 === 0,
          'image-left': index % 2 === 1
        }"
      >
        <div class="card-text-section">
          <div class="text-content">
            <div class="card-badge">{{ card.badge }}</div>
            <h2 class="card-title">{{ card.title }}</h2>
            <p class="card-description">{{ card.description }}</p>
            <ul class="card-features-list">
              <li v-for="(feature, i) in card.features" :key="i">
                <span class="check-icon">✓</span>
                {{ feature }}
              </li>
            </ul>
            <router-link to="/sasadmission">
              <div><button class="card-learn-btn">LEARN MORE</button></div>
            </router-link>
          </div>
        </div>
        <div class="card-image-section">
          <div class="image-placeholder">
            <img :src="card.image" :alt="card.title" class="card-image">
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import api from '@/services/api';
import { mediaUrl } from '@/services/media';

export default {
  name: 'Cards',
  data() {
    return {
      infoOpen: null,
      cards: [
        {
          badge: 'PRIMARY PROGRAMS', title: 'Building Strong Foundations',
          description: 'We believe that your child\'s learning should incorporate international mindedness and apply learning to global contexts.',
          features: ['Expert Native-speaker teachers', 'Modern learning facilities', 'Sports and arts programs', 'International curriculum'],
          image: require('../assets/01.jpg'), visible: false
        },
        {
          badge: 'SECONDARY PROGRAMS', title: 'Excellence in Education',
          description: 'Our secondary programs prepare students for higher education with advanced curriculum and personalized guidance.',
          features: ['Advanced curriculum', 'World-class facilities', 'University preparation', 'Career guidance counseling'],
          image: require('../assets/02.jpg'), visible: false
        },
        {
          badge: 'SPECIAL INITIATIVES', title: 'Innovation and Excellence',
          description: 'Through our special programs and initiatives, students explore talents in STEM, arts, and sports.',
          features: ['STEM excellence programs', 'Arts and music training', 'Sports development', 'Leadership workshops'],
          image: require('../assets/03.jpg'), visible: false
        },
        {
          badge: 'FLEXIBLE LEARNING', title: 'Digital Learning Solutions',
          description: 'Our online learning platform provides world-class education with flexibility and accessibility.',
          features: ['Live interactive classes', 'On-demand content library', '24/7 technical support', 'Flexible scheduling options'],
          image: require('../assets/04.jpg'), visible: false
        }
      ]
    }
  },
  async created() {
    try {
      // Call individual endpoint: /api/programs/
      const data = await api.getPrograms();
      const programs = api.extractList(data);
      if (programs.length > 0) {
        const defaultImages = [
          require('../assets/01.jpg'), require('../assets/02.jpg'),
          require('../assets/03.jpg'), require('../assets/04.jpg')
        ];
        this.cards = programs.map((p, i) => ({
          badge: p.badge || p.title,
          title: p.title,
          description: (p.description || '').replace(/<[^>]*>/g, ''),
          // Serializer: features[].title = alias for text
          features: (p.features || []).map(f => f.title || f.text || f),
          image: p.image ? mediaUrl(p.image) : defaultImages[i % defaultImages.length],
          visible: false
        }));
      }
    } catch (e) {
      console.warn('[Cards] API failed, using fallback:', e.message);
    }
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll);
    this.handleScroll();
    document.addEventListener('click', this.handleDocClick);
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.handleScroll);
    document.removeEventListener('click', this.handleDocClick);
  },
  methods: {
    handleScroll() {
      this.cards.forEach((card, index) => {
        const elements = document.querySelectorAll('.card-full-screen');
        if (elements[index]) {
          const rect = elements[index].getBoundingClientRect();
          card.visible = rect.top < window.innerHeight * 0.5 && rect.bottom > 0;
        }
      });
    },
    toggleInfo(index) { this.infoOpen = this.infoOpen === index ? null : index; },
    handleDocClick(e) { if (!e.target.closest('.card-image-section')) { this.infoOpen = null; } }
  }
}
</script>

<style scoped>
* { margin: 0; padding: 0; box-sizing: border-box; }
.cards-section { width: 100%; background: white; padding: 40px 16px; }
.cards-wrapper { width: 100%; display: flex; flex-direction: column; gap: 32px; }
.card-full-screen { width: 100%; max-width: 1200px; margin: 0 auto; min-height: 90vh; display: grid; grid-template-columns: 2fr 2fr; gap: 0; align-items: stretch; background: white; opacity: 0; padding: 0; transform: translateY(40px) scale(0.98); transition: all 1s cubic-bezier(0.34, 1.56, 0.64, 1); border-radius: 24px; overflow: hidden; box-shadow: 0 24px 60px rgba(15, 23, 42, 0.18); }
.card-full-screen.visible { opacity: 1; transform: translateY(0) scale(1); }
.card-full-screen.image-left { grid-template-columns: 2fr 2fr; }
.card-full-screen.image-left .card-image-section { order: -1; }
.card-text-section { display: flex; align-items: center; justify-content: center; padding: 80px 60px; background: transparent; }
.card-full-screen.image-right .card-text-section { background: #fff; }
.card-full-screen.image-left { background: linear-gradient(135deg, #c02131c9 0%, #f7f02bb9 100%); }
.card-full-screen.image-left .card-title, .card-full-screen.image-left .card-description, .card-full-screen.image-left .card-features-list li { color: #ffffff; }
.card-full-screen.image-left .check-icon { color: #F7F02B; }
.card-full-screen.image-left .card-badge { color: #fff; }
.text-content { max-width: 640px; }
.card-badge { display: inline-block; font-size: 11px; font-weight: 800; color: #C02130; letter-spacing: 2px; margin-bottom: 20px; text-transform: uppercase; background: linear-gradient(135deg, rgba(192, 33, 48, 0.1) 0%, rgba(247, 240, 43, 0.05) 100%); padding: 8px 16px; border-radius: 4px; }
.card-title { font-size: clamp(26px, 4vw, 42px); font-weight: 800; color: #2c3e50; line-height: 1.3; margin-bottom: 25px; letter-spacing: -1px; }
.card-description { font-size: clamp(14px, 1.6vw, 16px); color: #666; line-height: 1.7; margin-bottom: 30px; }
.card-features-list { list-style: none; margin-bottom: 30px; }
.card-features-list li { font-size: 14px; color: #666; font-weight: 500; padding: 8px 0; padding-left: 24px; position: relative; display: flex; align-items: center; line-height: 1.6; }
.check-icon { position: absolute; left: 0; color: #C02130; font-weight: 700; font-size: 16px; }
.card-learn-btn { padding: 16px 40px; background: linear-gradient(135deg, #C02130 0%, #F7F02B 100%); color: white; border: none; border-radius: 50px; font-weight: 700; font-size: 14px; cursor: pointer; transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1); letter-spacing: 0.5px; display: flex; align-items: center; gap: 8px; box-shadow: 0 10px 30px rgba(192, 33, 48, 0.2); }
.card-learn-btn::after { content: '→'; }
.card-learn-btn:hover { transform: translateY(-3px); box-shadow: 0 15px 40px rgba(192, 33, 48, 0.3); }
.card-image-section { display: flex; align-items: center; justify-content: center; background: linear-gradient(135deg, #C02130 0%, #F7F02B 100%); overflow: hidden; position: relative; transition: transform 0.9s cubic-bezier(0.19, 1, 0.22, 1); }
.image-placeholder { width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; position: relative; }
.card-image { width: 100%; height: 100%; object-fit: cover; position: relative; z-index: 1; transition: transform 1.1s cubic-bezier(0.19, 1, 0.22, 1), filter 0.6s ease-out; }
.card-image-section::before { content: ''; position: absolute; inset: -20%; background: radial-gradient(circle at top left, rgba(255, 255, 255, 0.35), transparent 55%), radial-gradient(circle at bottom right, rgba(0, 0, 0, 0.25), transparent 60%); opacity: 0; mix-blend-mode: soft-light; pointer-events: none; transition: opacity 0.6s ease-out; }
.card-full-screen:hover .card-image-section { transform: scale(1.02); }
.card-full-screen:hover .card-image { transform: scale(1.08); filter: saturate(1.1); }
.card-full-screen:hover .card-image-section::before { opacity: 1; }
@media (max-width: 1024px) { .card-full-screen { grid-template-columns: 1fr; min-height: auto; max-width: 960px; border-radius: 20px; } .card-full-screen.image-left .card-image-section { order: 0; } .card-text-section { padding: 60px 32px; min-height: auto; } .text-content { max-width: 100%; } .card-title { font-size: clamp(26px, 5vw, 36px); } .card-description { font-size: 15px; margin-bottom: 30px; } .card-image-section { min-height: 50vh; } }
@media (max-width: 768px) { .card-full-screen { grid-template-columns: 1fr; min-height: auto; margin: 12px auto; border-radius: 18px; box-shadow: 0 18px 40px rgba(15, 23, 42, 0.16); } .card-text-section { padding: 40px 24px; min-height: auto; } .card-badge { font-size: 11px; margin-bottom: 15px; } .card-title { font-size: 26px; margin-bottom: 20px; } .card-description { font-size: 14px; line-height: 1.7; margin-bottom: 25px; } .card-features-list li { font-size: 14px; padding: 8px 0; padding-left: 24px; } .card-learn-btn { padding: 14px 35px; font-size: 13px; } .card-full-screen:hover .card-image-section { transform: none; } .card-full-screen:hover .card-image { transform: none; filter: none; } }
@media (max-width: 480px) { .card-full-screen { grid-template-columns: 1fr; min-height: auto; margin: 12px auto; border-radius: 16px; box-shadow: 0 14px 32px rgba(15, 23, 42, 0.14); } .card-full-screen.image-left { grid-template-columns: 1fr; } .card-full-screen.image-left .card-image-section { order: 0; } .card-text-section { padding: 32px 18px; min-height: auto; } .text-content { max-width: 100%; } .card-badge { font-size: 10px; margin-bottom: 12px; } .card-title { font-size: 24px; margin-bottom: 15px; } .card-description { font-size: 13px; line-height: 1.6; margin-bottom: 20px; } .card-features-list { margin-bottom: 30px; } .card-features-list li { font-size: 13px; padding: 6px 0; padding-left: 24px; } .card-learn-btn { padding: 12px 30px; font-size: 12px; } .card-image-section { min-height: 50vh; width: 100%; } }
</style>
